import React from "react";
import { TFunction } from "i18next";

import { FormValues } from "./types";
import { CESAR_LOYALTY } from "@constants/index";

type Params = {
    values: FormValues;
    dataFidelity: any;
    fidelityData: any;
    dataArchive: any[];
    fidelityFranchise: any;
    dataFranchiseArchive: any[];
    oneShop: boolean;
    t: TFunction;
};

export function useFidelityExistence(params: Params) {
    const {
        values,
        dataFidelity,
        fidelityData,
        dataArchive,
        fidelityFranchise,
        dataFranchiseArchive,
        oneShop,
        t,
    } = params;

    const { choiceFidelity, auresKonnectGroup, designation } = values;

    const [isExist, setIsExist] = React.useState<boolean>(false);
    const [franchiseMatch, setFranchiseMatch] = React.useState<any>({});

    const checkIfFidelityExist = React.useCallback((): boolean => {
        if (fidelityData.data === undefined || dataArchive === undefined) {
            return false;
        }
        const allData: any[] = [...fidelityData.data, ...dataArchive];
        return allData.some(
            (el: any) =>
                (el[t("Loyalty")] === choiceFidelity &&
                    el[t("Group")] === auresKonnectGroup) ||
                (el.designation === designation &&
                    el.type === choiceFidelity &&
                    el.group === auresKonnectGroup) ||
                (el.designation === choiceFidelity &&
                    el.fidelityChoiceIndex !== CESAR_LOYALTY)
        );
    }, [
        fidelityData,
        dataArchive,
        choiceFidelity,
        auresKonnectGroup,
        designation,
        t,
    ]);

    const checkIfFidelityFranchiseExist = React.useCallback((): boolean => {
        const franchiseData = oneShop
            ? fidelityFranchise.othersData?.dataFranchise?.data
            : fidelityFranchise.data;
        if (franchiseData === undefined) return false;

        const match = franchiseData.find(
            (el: any) =>
                el[t("Loyalty")] === choiceFidelity &&
                ((el[t("Group")] === auresKonnectGroup &&
                    choiceFidelity.startsWith(t("Fidelity Softavera"))) ||
                    !choiceFidelity.startsWith(t("Fidelity Softavera")))
        );
        if (match) {
            setFranchiseMatch({ ...match });
            return true;
        }
        return false;
    }, [oneShop, fidelityFranchise, choiceFidelity, auresKonnectGroup, t]);

    const checkIfFidelityFranchiseExistAndArchive = React.useCallback((): boolean => {
        if (dataFranchiseArchive === undefined) return false;
        return dataFranchiseArchive.some(
            (el: any) =>
                el.type === choiceFidelity &&
                ((el.group === auresKonnectGroup &&
                    choiceFidelity.startsWith(t("Fidelity Softavera"))) ||
                    !choiceFidelity.startsWith(t("Fidelity Softavera")))
        );
    }, [dataFranchiseArchive, choiceFidelity, auresKonnectGroup, t]);

    React.useEffect(() => {
        const exists = checkIfFidelityExist();
        const sameLoyalty = dataFidelity[t("Loyalty")] === choiceFidelity;
        const sameGroup = dataFidelity[t("Group")] === auresKonnectGroup;
        const isCesarGroupChanged =
            !sameGroup && choiceFidelity.startsWith(t("Fidelity Softavera"));

        if (
            exists &&
            (dataFidelity.Id === undefined ||
                (dataFidelity.Id !== undefined &&
                    (!sameLoyalty || isCesarGroupChanged)))
        ) {
            setIsExist(true);
        } else {
            setIsExist(false);
        }
        // Why: deps track the user-visible inputs (designation, choiceFidelity,
        // auresKonnectGroup) + the edited record. `checkIfFidelityExist`,
        // `setIsExist` and the bare `choiceFidelity`/`auresKonnectGroup` aliases
        // are intentionally omitted — the callback already captures the latest
        // values via the destructured `values.*` props above, and listing the
        // useCallback closure would refresh the effect every render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        values.designation,
        values.choiceFidelity,
        values.auresKonnectGroup,
        t,
        dataFidelity,
    ]);

    return {
        isExist,
        franchiseMatch,
        checkIfFidelityFranchiseExist,
        checkIfFidelityFranchiseExistAndArchive,
    };
}
